'use client';

import { useRouter } from 'next/navigation';
import { ElementType, useContext, useEffect, useMemo, useState } from 'react';

import { FundingType, JourneyType, ProductType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import {
    type ActiveFiltersType,
    Cms,
    Comparator,
    Filter,
    MostSearched,
    PageHeader,
    ProductList,
    type ProductListProps,
    SortDisplayMode,
    type SortOptionType,
    Sorter,
    TopFilters,
} from '@bytel/product-wall';
import {
    addToActiveFilters,
    countActiveFiltersClearable,
    resetClearableActiveFilters,
} from '@bytel/product-wall/helpers';
import { type FilterType, FilterTypeEnum, type ProductInterface } from '@bytel/product-wall/types';
import {
    Accordion,
    Align,
    Button,
    ButtonVariant,
    Container,
    FlexBox,
    FlexItem,
    IconName,
    Justify,
    Link,
    Pagination,
    Section,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    View,
} from '@bytel/trilogy-react';

import { getAccordionSeo } from '@services/cms/accordion-seo';
import { getSlider } from '@services/cms/sliders';
import { getAppConfig } from '@services/config';
import { getWallCurrentPlan } from '@services/products';
import { stickersService } from '@services/products/stickers';
import { wallService } from '@services/products/wall';
import { getSessionVariable, setSessionVariable } from '@services/storage';

import { PalpatineFilterModel } from '@app-types/api/palpatine';
import { WallConfigType, WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { SearchParamsType } from '@app-types/page';
import { MobilePlanDetailWithProvenanceType, MobilePlanProvenanceType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { PageViewTrackingType } from '@app-types/tracking';
import { UserType, UserTypeEnum } from '@app-types/user';

import { ContractSelectionModal } from '@components/contract-selection-modal';
import { FundingLegals } from '@components/funding-legals';
import { HandsFree } from '@components/hands-free';
import { Konami } from '@components/konami';
import { Legals } from '@components/legals';
import { LinkCustom } from '@components/link-custom';
import { ProductDetailsModal } from '@components/product-details-modal';
import { ProductRedirectModal, type RedirectionOptionsType } from '@components/product-redirect-modal';
import { TriggerLogin } from '@components/trigger-login';
import { EmptyProductList } from '@components/walls';

import { getStoredComparator, storeComparator } from '@helpers/comparator';
import { setClientCookie } from '@helpers/cookies-client';
import { replaceWindowUrl, setDocumentTitle } from '@helpers/document';
import type { LegalsSlugType } from '@helpers/partials';
import { paths } from '@helpers/path';
import { formatPhoneNumber } from '@helpers/phone-number';
import { findProduct, getDisplayProducts } from '@helpers/product';
import { addSearchParams, generateHref, generateTitle, removeSearchParams, resetSearchParams } from '@helpers/seo';
import { sendPageViewTracking } from '@helpers/tracking';
import {
    sendAddToComparatorTracking,
    sendPageChangeTracking,
    sendPriceDetailTracking,
    sendRecommendedProductTracking,
    sendRedirectToComparatorTracking,
    sendRemoveFromComparatorTracking,
    sendResetComparatorTracking,
    sendResetFilterTracking,
    sendSortTracking,
    sendToggleComparatorTracking,
} from '@helpers/walls';

import { productsSkeleton } from '@constants/skeletons';

import { Breadcrumb, PlanSubtitle, ProductFilters } from '@product-wall/components';
import { getPhoneWallTrackingVariant, getStickersPostions } from '@product-wall/helpers';
import { useQuery } from '@tanstack/react-query';
import { PaginationProps } from '@trilogy-ds/react/lib/components/pagination/PaginationProps';

import { SmartChangePopover } from './smart-change-popover';

export type WallProps = {
    title: string;
    type: WallType;
    products: Product[];
    count: number;
    startPage: number;
    activeFilters?: ActiveFiltersType;
    availableSorts?: SortOptionType[];
    activeSort?: string;
    pageSize?: number;
    stickers?: ProductListProps['stickers'];
    wallConfig: WallConfigType;
    filters: FilterType[];
    searchParams?: SearchParamsType;
    slider?: string;
    accordionSeo?: string;
    enableKonami?: boolean;
    enableHandsFree?: boolean;
    plan?: MobilePlanDetailWithProvenanceType;
    search?: string;
    isRenewal?: boolean;
    user?: UserType;
    contract?: ContractType;
    trackingData?: PageViewTrackingType;
    enableCompare?: boolean;
    isLoading?: boolean;
    imageAs?: ElementType;
    slug: LegalsSlugType;
    palpatineModelFilters?: PalpatineFilterModel;
};

const appConfig = getAppConfig();

export function Wall({
    title: _title,
    type,
    products: _products,
    count: _count,
    startPage,
    activeFilters: _activeFilters = {},
    activeSort = 'meilleures-ventes',
    pageSize = 28,
    stickers: _stickers = {},
    wallConfig,
    filters: _filters,
    searchParams: _searchParams,
    slider: _slider,
    accordionSeo: _accordionSeo,
    enableKonami = false,
    enableHandsFree = false,
    isRenewal = false,
    user,
    contract,
    plan: _plan,
    search,
    trackingData,
    enableCompare = false,
    isLoading: _isLoading = false,
    imageAs: Image,
    slug,
    palpatineModelFilters,
}: Readonly<WallProps>) {
    const { cart } = useContext(CartContext);
    const router = useRouter();

    // --- States ---

    const [currentPage, setCurrentPage] = useState(startPage);
    const [currentFilters, setCurrentFilters] = useState(_activeFilters);
    const [currentSort, setCurrentSort] = useState(activeSort);
    const [currentSearch, setCurrentSearch] = useState(search);
    const [comparedProducts, setComparedProducts] = useState<ProductInterface[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
    const [isContractSelectionModalOpen, setIsContractSelectionModalOpen] = useState(false);
    const [redirectUrls, setRedirectUrls] = useState<RedirectionOptionsType | undefined>();
    const [searchParams, setSearchParams] = useState<URLSearchParams>(() => {
        if (_searchParams) return new URLSearchParams(_searchParams);
        if (typeof window !== 'undefined') return new URLSearchParams(window.location.search);
        return new URLSearchParams();
    });

    // --- Memos ---

    const isCdc = useMemo(() => user?.user_type === UserTypeEnum.CDC, [user]);

    const filters = useMemo(() => {
        if (searchParams.get('forfait') === 'sans') {
            return _filters.filter((f) => f.key !== 'sousType');
        }
        return _filters;
    }, [_filters, searchParams]);

    const cartJourney = useMemo(
        () => cart?.getJourneyOfType(isRenewal ? JourneyType.RENEWAL : JourneyType.MOBILE_ACQUISITION),
        [cart],
    );

    const cartPlan = useMemo(() => cartJourney?.getProductsByType(ProductType.MAIN_MOBILE_OFFER)?.[0], [cartJourney]);

    const mostSearchedOptions = useMemo((): FilterType[] => {
        if (type !== WallType.PHONE) {
            return [];
        }

        const brandFilter = wallConfig.filtersObject['marque'];
        if (brandFilter?.type === FilterTypeEnum.CHECKBOX_LIST) {
            return [
                {
                    ...brandFilter,
                    values: brandFilter.values.filter((value) => value.isMostSearched),
                },
            ];
        }

        return [];
    }, [wallConfig]);

    // --- Queries ---

    const currentPlanQuery = useQuery({
        enabled: type === WallType.PHONE,
        queryKey: [
            'wall-current-plan',
            type,
            searchParams,
            cartPlan?.gencode,
            isRenewal,
            contract,
            user,
            currentFilters?.sousType?.length,
        ],
        queryFn: () =>
            getWallCurrentPlan(
                type,
                searchParams,
                cartPlan,
                isRenewal,
                contract,
                user,
                Boolean(currentFilters?.sousType?.length),
            ),
        initialData: _plan,
    });

    const stickersQuery = useQuery({
        enabled: type === WallType.PHONE,
        queryKey: ['wall-stickers', currentFilters, isRenewal, currentSearch],
        queryFn: () => stickersService.get(currentFilters, isRenewal, currentSearch ?? null),
        initialData: _stickers,
    });

    const productsQuery = useQuery({
        queryKey: [
            'wall-products',
            type,
            currentSort,
            currentPage,
            pageSize,
            currentFilters,
            currentPlanQuery.data?.plan,
            searchParams.get('nom'),
            contract,
            stickersQuery.data,
        ],
        queryFn: () =>
            wallService
                .getProduct(type, {
                    sort: currentSort,
                    page: currentPage,
                    limit: pageSize,
                    filters: currentFilters,
                    plan: currentPlanQuery.data?.plan?.gencode ?? (currentPlanQuery.data?.plan as undefined | null),
                    search: searchParams?.get('nom') ?? undefined,
                    contract: contract?.id,
                    stickers: stickersQuery.data ? getStickersPostions(stickersQuery.data) : undefined,
                })
                .then((result) => ({
                    products: getDisplayProducts(result.products, currentPlanQuery.data, contract),
                    count: result.count,
                })),
        initialData: { products: _products, count: _count },
    });

    const sliderQuery = useQuery({
        enabled: type === WallType.PHONE,
        queryKey: ['wall-slider', wallConfig, currentFilters, isRenewal],
        queryFn: () => getSlider(wallConfig, currentFilters, isRenewal),
        initialData: _slider,
    });

    const accordionSeoQuery = useQuery({
        enabled: type === WallType.PHONE,
        queryKey: ['wall-accordion-seo', wallConfig, currentFilters, isRenewal],
        queryFn: () => getAccordionSeo(wallConfig, currentFilters),
        initialData: _accordionSeo,
    });

    // --- Derived Memos ---

    const isLoading = useMemo(() => _isLoading || productsQuery.isFetching, [_isLoading, productsQuery?.isFetching]);

    const title = useMemo(
        () => generateTitle(_title, undefined, { activeFilters: currentFilters, wallConfig }),
        [_title, currentPage, currentFilters, wallConfig],
    );

    const pageViewTrackingData = useMemo(() => {
        if (!trackingData || type !== WallType.PHONE) {
            return trackingData;
        }

        return {
            ...trackingData,
            variant: {
                Recommandation: trackingData.variant?.Recommandation ?? false,
                ...getPhoneWallTrackingVariant(currentFilters, wallConfig),
            },
        };
    }, [currentFilters, trackingData, type, wallConfig]);

    // --- Handlers ---

    const href = (name: string, value: string, active = currentFilters, urlSearch = searchParams): string => {
        if (name === '') {
            return wallConfig.baseUrl;
        }
        return `${wallConfig.baseUrl}/${generateHref(
            wallConfig,
            addToActiveFilters(active, name, value),
            urlSearch,
            false,
        )}`;
    };

    const hrefSearchParams = (key: string, value: string) => {
        let newSearchParams = new URLSearchParams(searchParams);
        if (!value) {
            newSearchParams = removeSearchParams(key, newSearchParams);
        } else {
            newSearchParams = addSearchParams(key, value, newSearchParams);
        }
        return href(key, value, currentFilters, newSearchParams);
    };

    const hrefForSearch = (value: string): string => {
        let searchParamsWithSearch = new URLSearchParams(searchParams);
        const hasSameSearch = searchParamsWithSearch.get('nom') === value;
        if (hasSameSearch) {
            searchParamsWithSearch = removeSearchParams('nom', searchParamsWithSearch);
        } else {
            searchParamsWithSearch = addSearchParams('nom', value, searchParamsWithSearch);
        }
        return href('nom', value, undefined, searchParamsWithSearch);
    };

    const setHasSelectedWallRedirection = () => {
        setRedirectUrls(undefined);
        setSessionVariable('hasSelectedWallRedirection', 'true');
    };

    const onTopFilterClick = (key: string, value: string) => {
        let newSearchParams: URLSearchParams;
        if (!value) {
            newSearchParams = removeSearchParams(key, searchParams);
        } else {
            newSearchParams = addSearchParams(key, value, searchParams);
        }

        return onFilter(currentFilters, newSearchParams);
    };

    const onFilter = async (activeFilters: ActiveFiltersType, urlSearchParams?: URLSearchParams) => {
        setCurrentPage(1);
        setCurrentFilters(activeFilters);
        if (urlSearchParams) {
            setSearchParams(urlSearchParams);
        }
    };

    const onSearch = async (value: string) => {
        setCurrentPage(1);
        setCurrentSearch(value);
        setSearchParams(addSearchParams('nom', value, searchParams));
    };

    const onRemoveSearch = async () => {
        setCurrentPage(1);
        setCurrentSearch('');
        setSearchParams(removeSearchParams('nom', searchParams));
    };

    const onChangePage: PaginationProps['onClick'] = async (event) => {
        event?.preventDefault();
        setCurrentPage(event?.currentPage);
        sendPageChangeTracking(isRenewal);
        setSearchParams(addSearchParams('page', event?.currentPage.toString(), searchParams));
    };

    const onSort = async (sort: string | undefined) => {
        if (sort) {
            setCurrentPage(1);
            setCurrentSort(sort);
            setSearchParams(addSearchParams('sort', sort, searchParams));
            sendSortTracking(wallConfig, isRenewal, sort);
        } else {
            setSearchParams(removeSearchParams('sort', searchParams));
        }
    };

    const onSelectProduct = ({ id }: ProductInterface) => {
        const product = findProduct(productsQuery.data.products, id);
        setSelectedProduct(product);
        sendPriceDetailTracking(isRenewal, type);
    };

    const onProductClick = (product: ProductInterface) => {
        const params = new URLSearchParams();
        if (currentPlanQuery.data) {
            if (currentFilters?.sousType?.length) {
                params.set('sousType', 'smart-change');
            }

            if (currentPlanQuery.data.origin === MobilePlanProvenanceType.URL) {
                params.set('forfait', currentPlanQuery.data.plan?.urlKey ?? 'sans');
            }
        }
        const planQuery = params.toString() ? `?${params.toString()}` : '';
        if (isRenewal || getSessionVariable('hasSelectedWallRedirection')) {
            router.push(`${product.url}${planQuery}`);
        } else {
            setRedirectUrls({
                acquisition: { url: paths.phoneDetail(`${product.id}${planQuery}`) },
                renewal: { url: paths.phoneDetail(`${product.id}${planQuery}`, true) },
            });
        }
    };

    const onCompare = (product: ProductInterface) => {
        let newComparedProducts = [...comparedProducts];
        if (comparedProducts.some((p) => p.gencode === product.gencode)) {
            newComparedProducts = newComparedProducts.filter((p) => p.gencode !== product.gencode);
        } else {
            newComparedProducts.push(product);
        }
        sendAddToComparatorTracking(isRenewal);
        setComparedProducts(newComparedProducts);
        storeComparator(newComparedProducts);
    };

    const resetFilters = () => {
        setCurrentSearch('');
        const newSearchParams = resetSearchParams(searchParams, ['sort']);
        void onFilter(resetClearableActiveFilters(wallConfig.filters, currentFilters), newSearchParams);
        sendResetFilterTracking(isRenewal);
    };

    const scrollToTop = () => {
        window.document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
    };

    // --- Effects ---

    useEffect(() => {
        if (pageViewTrackingData) {
            sendPageViewTracking(isRenewal, pageViewTrackingData);
        }
    }, [isRenewal, pageViewTrackingData]);

    useEffect(() => {
        setDocumentTitle(
            generateTitle(
                _title,
                { currentPage, total: productsQuery.data.count, pageSize },
                { activeFilters: currentFilters, wallConfig },
            ),
        );
    }, [_title, currentPage, productsQuery.data.count, pageSize, currentFilters, wallConfig]);

    useEffect(() => {
        const generatedUrl = generateHref(wallConfig, currentFilters, searchParams, true);
        let url = `${wallConfig.baseUrl}/${isRenewal ? 'renouvellement/' : ''}`;
        if (generatedUrl.startsWith('?')) {
            url = url.slice(0, -1);
        }
        url += generatedUrl;
        if (url.endsWith('/')) {
            url = url.slice(0, -1);
        }

        replaceWindowUrl(url);
    }, [wallConfig, currentFilters, searchParams]);

    useEffect(() => {
        if (enableCompare) {
            const newComparedProducts = getStoredComparator();
            setComparedProducts(newComparedProducts);
        }
    }, [enableCompare]);

    useEffect(() => {
        if (isRenewal) {
            const isPremiumRenewal = contract?.subscription.isPremium ?? false;
            productsQuery.data.products
                .filter((product) => product.recommended)
                .forEach((product) => {
                    sendRecommendedProductTracking(isRenewal, isPremiumRenewal, product);
                });
        }
    }, [productsQuery.data.products]);

    useEffect(() => {
        if (currentPage) scrollToTop();
    }, [currentPage]);

    useEffect(() => {
        setClientCookie(`${type}WallFilter`, generateHref(wallConfig, currentFilters, searchParams, false));
    }, [wallConfig, currentFilters, searchParams]);

    return (
        <>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <PageHeader
                    title={
                        isRenewal ? (
                            <>
                                Bonjour
                                {!isCdc && (
                                    <>
                                        {' '}
                                        <span className='is-capitalized'>{user?.given_name?.toLocaleLowerCase()}</span>,
                                    </>
                                )}
                            </>
                        ) : (
                            title
                        )
                    }
                    subtitle={
                        isRenewal && !isCdc ? (
                            <>
                                changez de téléphone pour la ligne{' '}
                                <Link
                                    role='button'
                                    aria-label='Choisir une autre ligne'
                                    onClick={() => setIsContractSelectionModalOpen(true)}
                                >
                                    {formatPhoneNumber(contract?.phoneNumber ?? '')}
                                </Link>
                            </>
                        ) : null
                    }
                />
                <TriggerLogin />
            </Section>
            <Cms content={sliderQuery.data} className='has-background-grey-fade' />
            <Section id='products' backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                {wallConfig.topFilters && !isRenewal && (
                    <TopFilters
                        topFilters={wallConfig.topFilters}
                        href={hrefSearchParams}
                        onChange={onTopFilterClick}
                        activeFilters={
                            searchParams.get('forfait') === 'sans'
                                ? addToActiveFilters({}, 'forfait', 'sans')
                                : currentFilters
                        }
                    />
                )}
                <View flexable justify={Justify.SPACE_BETWEEN} align={Align.CENTER} className='gap-medium flex-wrap'>
                    <View
                        flexable
                        align={Align.CENTER}
                        fullwidth={false}
                        className='gap-medium is-spaced-between-mobile is-fullwidth-mobile'
                    >
                        <Title level={TitleLevels.FOUR} markup={TitleMarkup.H2} marginless className='no-wrap'>
                            Filtrez et triez
                        </Title>
                        {countActiveFiltersClearable(filters, currentFilters) > 0 && (
                            <Button
                                variant={ButtonVariant.GHOST}
                                onClick={resetFilters}
                                iconName={IconName.UNDO}
                                className='w-fit'
                            >
                                &nbsp;Réinitialiser les filtres
                            </Button>
                        )}
                    </View>
                </View>
                <View className='sticky-padding is-sticky-top py-small has-background-grey-fade'>
                    <FlexBox align={Align.CENTER}>
                        <FlexItem size={{ tablet: 12, desktop: 10 }}>
                            {filters.length > 0 && (
                                <Filter
                                    onFilter={onFilter}
                                    availableFilters={filters}
                                    activeFilters={currentFilters}
                                    href={href}
                                    linkAs={LinkCustom}
                                    resetFilters={resetFilters}
                                    count={productsQuery.data.count}
                                >
                                    <MostSearched
                                        availableFilters={mostSearchedOptions}
                                        activeFilters={currentFilters}
                                        scrollable={false}
                                        onFilter={onFilter}
                                        linkAs={LinkCustom}
                                        href={href}
                                    />
                                    {wallConfig.sorter && (
                                        <Sorter
                                            label='Trier par'
                                            onSort={onSort}
                                            availableSorts={wallConfig.sorter}
                                            activeSort={currentSort}
                                            displayAs={SortDisplayMode.ACCORDION}
                                        />
                                    )}
                                </Filter>
                            )}
                            {filters.find((f) => f.key === 'sousType' && f.isVisible !== false) && (
                                <SmartChangePopover />
                            )}
                        </FlexItem>
                        {wallConfig.sorter && (
                            <FlexItem size={{ desktop: 2 }} className='is-hidden-touch'>
                                <Sorter
                                    label='Trier par'
                                    onSort={onSort}
                                    availableSorts={wallConfig.sorter}
                                    activeSort={currentSort}
                                    displayAs={SortDisplayMode.SELECT}
                                />
                            </FlexItem>
                        )}
                    </FlexBox>
                </View>
                <Spacer size={SpacerSize.TWO} />
                <MostSearched
                    availableFilters={mostSearchedOptions}
                    activeFilters={currentFilters}
                    scrollable
                    onFilter={onFilter}
                    linkAs={LinkCustom}
                    href={href}
                    backgroundColor={TrilogyColor.BACKGROUND}
                    fullwidth={false}
                    inline
                />
                {type === WallType.PHONE ? <PlanSubtitle planWithProvenance={currentPlanQuery.data} /> : null}
                <Text
                    level={TextLevels.THREE}
                    aria-live='polite'
                    aria-atomic='true'
                    accessibilityLabel={`${productsQuery.data.count} ${wallConfig.productType.toLowerCase()}${productsQuery.data.count > 1 ? 's' : ''} disponible${productsQuery.data.count > 1 ? 's' : ''}`}
                >
                    {wallConfig.productType}
                    {productsQuery.data.count > 1 ? 's' : ''} disponible
                    {productsQuery.data.count > 1 ? 's' : ''} ({productsQuery.data.count})
                </Text>
                <ProductList
                    isLoading={isLoading}
                    products={isLoading ? productsSkeleton : productsQuery.data.products}
                    imageAs={isLoading ? 'img' : Image}
                    stickers={productsQuery.data.count > 0 ? stickersQuery.data : {}}
                    firstElementIndex={(currentPage - 1) * pageSize}
                    openPriceDetail={onSelectProduct}
                    linkAs={LinkCustom}
                    onClick={type === WallType.PHONE ? onProductClick : undefined}
                    onCompare={enableCompare ? onCompare : undefined}
                    comparedProducts={comparedProducts}
                    disableCompare={() => comparedProducts.length >= 3}
                    data-cy='product-list'
                />
                {productsQuery.data.count <= 0 && !isLoading && <EmptyProductList resetFilters={resetFilters} />}
                <Spacer size={SpacerSize.FIVE} />
                {pageSize < productsQuery.data.count && (
                    <Pagination
                        onClick={onChangePage}
                        length={Math.ceil(productsQuery.data.count / pageSize)}
                        defaultPage={currentPage}
                        href={(page) =>
                            page < 1 || page > Math.ceil(productsQuery.data.count / pageSize)
                                ? ''
                                : `?${addSearchParams('page', page.toString(), searchParams).toString()}`
                        }
                    />
                )}
                <Spacer size={SpacerSize.EIGHT} />
                <ProductFilters
                    palpatineModelFilters={palpatineModelFilters}
                    currentSearch={currentSearch}
                    onRemoveSearch={onRemoveSearch}
                    onSearch={onSearch}
                    hrefForSearch={hrefForSearch}
                />
            </Section>
            {comparedProducts.length > 0 && (
                <Comparator
                    products={comparedProducts}
                    onOpen={() => sendToggleComparatorTracking(isRenewal, true)}
                    onClose={() => sendToggleComparatorTracking(isRenewal, false)}
                    onReset={() => {
                        sendResetComparatorTracking(isRenewal);
                        storeComparator([]);
                        setComparedProducts([]);
                    }}
                    onProductDelete={(newComparedProducts) => {
                        sendRemoveFromComparatorTracking(isRenewal);
                        storeComparator(newComparedProducts);
                        setComparedProducts(newComparedProducts);
                    }}
                    compareHref={(products) =>
                        paths.mobileComparator(
                            isRenewal,
                            products.map((p) => p.gencode),
                        )
                    }
                    onCompare={(data) => {
                        if (data.href) {
                            sendRedirectToComparatorTracking(isRenewal);
                            router.push(data.href);
                        }
                    }}
                    defaultImage={`${appConfig.assets.cmsImages.url}/original/image/CMS/ecommerce/wall/lazyload-phone-default-image.svg`}
                />
            )}
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <Cms content={accordionSeoQuery.data} />
                    {enableHandsFree && (
                        <>
                            <HandsFree />
                            <Spacer size={SpacerSize.FOUR} />
                        </>
                    )}
                    <Breadcrumb wallConfig={wallConfig} activeFilters={currentFilters} />
                    {type === WallType.PHONE && (
                        <Accordion>
                            <FundingLegals type={FundingType.YOUNITED} />
                            <Legals id='wall-legals' url={slug} isItem />
                        </Accordion>
                    )}
                </Container>
            </Section>
            {(isContractSelectionModalOpen || (isRenewal && !contract)) && (
                <ContractSelectionModal
                    selected={contract?.id}
                    onClose={() => setIsContractSelectionModalOpen(false)}
                />
            )}
            {enableKonami && (
                <Konami
                    action={() => {
                        document.cookie = 'konamiEnabled=true;max-age=86400';
                        router.push('/telephones-mobiles/2002');
                    }}
                />
            )}
            {selectedProduct && (
                <ProductDetailsModal
                    currentProduct={selectedProduct}
                    selectedFunding={selectedProduct.details.fundings[0]}
                    onClose={() => setSelectedProduct(undefined)}
                    type={type}
                    plan={currentPlanQuery.data?.plan ?? undefined}
                    modalOpen={true}
                />
            )}
            {redirectUrls && <ProductRedirectModal onAction={setHasSelectedWallRedirection} options={redirectUrls} />}
        </>
    );
}
